/** Where two versions of the draft differ: [start, removedEnd) of the old became [start, insertedEnd). */
export interface TextChange {
  start: number;
  removedEnd: number;
  insertedEnd: number;
}

export function diffText(before: string, after: string): TextChange {
  const shorter = Math.min(before.length, after.length);
  let start = 0;
  while (start < shorter && before[start] === after[start]) start += 1;
  let tail = 0;
  while (
    tail < shorter - start &&
    before[before.length - 1 - tail] === after[after.length - 1 - tail]
  ) {
    tail += 1;
  }
  return { start, removedEnd: before.length - tail, insertedEnd: after.length - tail };
}

/** Moves an offset in the old text to the same place in the new one; inside the change, to its end. */
export function mapOffset(offset: number, change: TextChange): number {
  if (offset <= change.start) return offset;
  if (offset >= change.removedEnd) return offset + change.insertedEnd - change.removedEnd;
  return change.insertedEnd;
}

/** A caret offset from the wire, inside [0, length], or null when it is not a number. */
export function clampOffset(value: unknown, length: number): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return Math.min(Math.max(Math.round(value), 0), length);
}

/** A selection from the wire, ordered and inside the text, or null when it is empty or malformed. */
export function clampRange(value: unknown, length: number): [number, number] | null {
  if (!Array.isArray(value) || value.length !== 2) return null;
  const a = clampOffset(value[0], length);
  const b = clampOffset(value[1], length);
  if (a == null || b == null || a === b) return null;
  return a < b ? [a, b] : [b, a];
}
